import React, { useCallback, useEffect, useMemo } from 'react';
import {
    fetchPartners,
    selectSort,
    selectPartnersTableData,
    selectIsLoading,
} from 'ducks/partners';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { routes } from 'constants/common';
import { Action, TableComponent } from 'components/common/Table';
import { Partner } from 'ducks/partners/types';
import { getColumns } from './columns';
import { Pagination } from './Pagination';
import { SearchToolbar } from './SearchToolbar';
import { FullScreenLoaderComponent } from '../../common/Loader/FullScreenLoader';

export const PartnersList: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const tableData = useSelector(selectPartnersTableData);
    const sort = useSelector(selectSort);
    const loading = useSelector(selectIsLoading);
    const columns = useMemo(() => getColumns(t, dispatch), [t, dispatch]);

    useEffect(() => {
        dispatch(fetchPartners());
    }, [dispatch]);

    const redirectToPartner = useCallback(
        (id: number) =>
            dispatch(push(routes.PARTNER.replace(':partnerId', `${id}`))),
        [dispatch],
    );

    const actions = React.useMemo(
        (): Action<Partner>[] => [
            {
                icon: 'visibility',
                tooltip: t('tripList.seeDetails'),
                onClick: (event, rowData: Partner | undefined) =>
                    rowData && redirectToPartner(rowData.id),
            },
        ],
        [redirectToPartner, t],
    );

    return (
        <>
            {loading && <FullScreenLoaderComponent />}

            <TableComponent
                actions={actions}
                onRowClick={(event, rowData: Partner | undefined) =>
                    rowData && redirectToPartner(rowData.id)
                }
                searchComponent={SearchToolbar}
                columns={columns}
                pagination={Pagination}
                data={tableData}
                title={t('partners.title')}
                sort={sort}
            />
        </>
    );
};
